import styled from "@emotion/styled"
import { motion, useTransform, transform, MotionValue } from "framer-motion"

import { buttonFilled } from "css/buttons"
import { contrastMode } from "css/a11y"
import getMediaQuery from "css/breakpoints"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"

import useSSR from "hooks/useSSR"

import generateId from "utils/generateId"

import { cssVars } from "components/nav-v2/Wrapper"
import VideoLauncher from "components/video-launcher/VideoLauncher"
import { pageHeaderCSS, kickerCN, subtitleCN } from "components/page-heading/style"
import OysterStoryLogo from "components/oyster-story-logo/OysterStoryLogo"
import { legend100, legend120 } from "css/text"

const Header = styled(motion.header)`
  ${pageHeaderCSS}
  margin-top: 0;
  grid-template-rows: [kicker subtitle] min-content [mid-line] min-content [title video-cta];
  position: sticky;
  top: calc((100vh - var(${cssVars.main.safe})) * 0.44);
  height: 0;

  > button,
  .cta-cont button {
    ${buttonFilled}
  }

  > button,
  & .cta-cont {
    grid-row: video-cta;
    grid-column: main;
    justify-self: center;
    margin-block-start: 50px;
  }

  ${getMediaQuery("m")} {
    & h1 {
      ${({ iscoverwot }) =>
        iscoverwot &&
        `
      grid-column: col 3/span 8;
    `}
    }
    &.more-width h1 {
      grid-column: col 2 / span 10;
    }
  }

  & h1 .txttranslation {
    ${legend100}
    display:block;
  }

  & h1,
  & p {
    position: relative;
    text-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
    ${contrastMode}
  }

  & h1 > svg {
    margin-block-start: 0.6rem;
    width: calc(var(--grid-col-unit) * 4);

    ${getMediaQuery("m")} {
      width: calc(var(--grid-col-unit) * 6);
    }
    ${getMediaQuery("l")} {
      width: calc(var(--grid-col-unit) * 4);
    }
  }
`

const variants = {
  show: { opacity: 1, y: 0, transition: { ease: "easeOut", duration: 0.7 } },
  hide: { opacity: 0, y: 30 },
}

export default function PageTitleBlock({
  iscoverwot,
  title,
  subtitle,
  kicker,
  videoCTA,
  prog,
  use_os_logo,
  animated_logo,
  logo_translation,
  increase_heading_width,
  theme = "dark-theme",
}) {
  const console = useConsole()
  const ssr = useSSR()

  const { height } = useViewport()
  const _height = ssr ? new MotionValue(0) : height /** first render as SERVER ( height=0 ) */

  const opacity = useTransform([prog, _height], ([p, h]) => transform(p, [0, h * 0.1], [1, 0]))
  const visibility = useTransform(opacity, o => (o === 0 ? "hidden" : "visible"))

  const titleId = generateId("title")

  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")

  const cn = [theme, increase_heading_width ? "more-width" : ""].filter(Boolean).join(" ")

  return (
    <Header className={cn} iscoverwot={iscoverwot} style={{ opacity, visibility }} initial='hide' animate='show' transition={{ staggerChildren: 0.45 }}>
      {/* {kicker && <p className={kickerCN}>{kicker}</p>} */}
      {kicker ? (
        animated_logo && !prefersReducedMotion ? (
          <motion.p className={kickerCN} variants={variants}>
            {kicker}
          </motion.p>
        ) : (
          <p className={kickerCN}>{kicker}</p>
        )
      ) : null}
      {use_os_logo ? (
        <h1 id={titleId}>
          <OysterStoryLogo animated_logo={animated_logo} />
          {logo_translation ? <span className='txttranslation'>{logo_translation}</span> : null}
        </h1>
      ) : (
        <h1 dangerouslySetInnerHTML={{ __html: title }} id={titleId} />
      )}
      {subtitle && <p className={subtitleCN}>{subtitle}</p>}
      {!!videoCTA && animated_logo && !prefersReducedMotion ? (
        <motion.div className='cta-cont' variants={variants}>
          <VideoLauncher {...videoCTA} titleId={titleId} />
        </motion.div>
      ) : videoCTA ? (
        <VideoLauncher {...videoCTA} titleId={titleId} />
      ) : null}
    </Header>
  )
}
